import React, {useCallback, useMemo, useRef, useState} from 'react'
import {translate} from 'i18n'
import {requestData} from 'services'
import {
  FORKYGRAM_FEED_QUERY_LIMIT,
  FORKYGRAM_FAVORITES_QUERY_LIMIT,
  FORKYGRAM_FOOTPRINTS_QUERY_LIMIT,
  FORKYGRAM_FOOTPRINTS_MAX_ITEM,
} from 'consts'
import {
  TreeFotoTreeLeafFavorite,
  TreeFotoTreeLeafFootprintData,
  TreeFotoTreeLeafFootprintTree,
} from 'types'
import {
  getContentURL,
  showSnackbar,
  useDidMount,
  useDidUpdate,
  useHistory,
  useLocation,
} from 'utils'
import {useWindowMode} from 'windows'
import {ButtonHeader, ListNotLazy} from 'common/components'
import {useSelector} from 'lib/redux'
import {GiftShopRootScreen} from 'pages/gift-shop'
import {
  ForkygramListGridItem,
  ForkygramTemplateListGridContent,
} from '../../template'
import {ForkygramFootprintPagination} from '../container'

export default function ForkygramFootprintDetailScreen() {
  const mode = useWindowMode()
  const history = useHistory()
  const location = useLocation('forkygram_footprint_detail')
  const {memberId, tagId, treeId, treeName, treeAlias} = location.state
  const {page: pageQuery} = location.query
  const {id} = useSelector('user') || {}
  const footprintLastPage = useRef(1)
  const favoriteLastPage = useRef({page: 1, isEnd: false})
  const stateData = useState<ReadonlyArray<TreeFotoTreeLeafFootprintData>>([])
  const statePage = useState(Number(pageQuery) || 1)
  const data = stateData[0]
  const rawPage = statePage[0]
  const [count, setCount] = useState(0)
  const [isPinned, setPinned] = useState(false)
  const [loading, setLoading] = useState(false)
  const column = mode === 'website' ? 5 : 4

  const pageCount = useMemo(
    () => Math.ceil(count / FORKYGRAM_FOOTPRINTS_QUERY_LIMIT),
    [count],
  )

  const handleNavigateFotoTreeDetail = useCallback(
    (item: TreeFotoTreeLeafFootprintData) => {
      const index = data.findIndex((val) =>
        val.type === 'fotoyu' && item.type === 'fotoyu'
          ? val.content_id === item.content_id
          : val.type === 'footprint' && item.type === 'footprint'
          ? val.post_id === item.post_id
          : 1,
      )

      const basePage = Math.floor(index / FORKYGRAM_FEED_QUERY_LIMIT) + 1
      const currPage = basePage + (footprintLastPage.current - 1) * 2

      history.push(
        'tree_fototree_feed',
        {
          tagId,
          treeId,
          treeAlias,
          memberId,
          selectedItemId:
            item.type === 'fotoyu' ? item.content_id : item.post_id,
          initialPage:
            item.type === 'fotoyu'
              ? footprintLastPage.current *
                (footprintLastPage.current === 1 ? 1 : 2)
              : currPage,
          initialPageFavorite:
            favoriteLastPage.current.page === footprintLastPage.current
              ? favoriteLastPage.current.page
              : undefined,
        },
        treeAlias,
      )
    },
    [data, history, memberId, tagId, treeAlias, treeId],
  )

  const handleUpdatePage = useCallback(
    (page: number) => {
      history.replaceQuery({
        path: 'forkygram_footprint_detail',
        state: location.state,
        queryObject: {
          page,
        },
      })
    },
    [history, location],
  )

  const handleClickHeader = useCallback(() => {
    history.push('tree_fototree_detail', {treeId, treeAlias}, treeAlias)
  }, [history, treeAlias, treeId])

  const handleRenderItem = useCallback(
    (item: TreeFotoTreeLeafFootprintData) =>
      item.type === 'fotoyu' ? (
        <ForkygramListGridItem
          borderColor="black"
          column={column}
          data={{
            contentType: item.content_type || 'photo',
            url: getContentURL({url: item.url, type: item.type}),
            thumbnail: getContentURL({url: item.url, type: item.type}),
            isHidden: true,
            isWishlist: true,
          }}
          onClickItem={() => handleNavigateFotoTreeDetail(item)}
        />
      ) : (
        <ForkygramListGridItem
          borderColor="black"
          column={column}
          data={{
            contentType: item.post.content_type,
            url: item.url,
            thumbnail: item.url,
            isHidden: item.is_hidden,
          }}
          onClickItem={() => handleNavigateFotoTreeDetail(item)}
        />
      ),
    [column, handleNavigateFotoTreeDetail],
  )

  const handleLoadFootprintData = useCallback(
    async (
      page: number,
      limit: number,
    ): Promise<TreeFotoTreeLeafFootprintTree[] | null> => {
      const response = await requestData('tree_get_leaf_footprints', {
        useDefaultMessage: true,
        actionType: 'fetch',
        params: {page, limit, member_id: memberId, tree_id: treeId},
        onRequestSuccess: ({status, data: {result}}) => {
          if (status === 200) {
            setCount(result.count)
            setPinned(!!result.footprint_pinned_at)
          }
        },
      })

      footprintLastPage.current = page

      return typeof response !== 'string' && response.status === 200
        ? response.data.result.footprints.map((item) => ({
            ...item,
            type: 'footprint',
          }))
        : null
    },
    [memberId, treeId],
  )

  const handleLoadFavoriteData = useCallback(
    async (
      page: number,
      limit: number,
    ): Promise<TreeFotoTreeLeafFavorite[] | null> => {
      if (id !== memberId) return []

      const {current} = favoriteLastPage

      if (page <= current.page) current.isEnd = false
      if (current.isEnd && current.page !== page) return []

      const response = await requestData('giftshop_get_favorite', {
        useDefaultMessage: true,
        actionType: 'fetch',
        params: {page, limit, tag_id: tagId},
      })

      if (
        !response ||
        typeof response === 'string' ||
        response.status !== 200
      ) {
        return null
      }

      const {result} = response.data
      current.page = page
      current.isEnd = result.length < limit

      return result.filter(
        ({type}) => type === 'fotoyu',
      ) as TreeFotoTreeLeafFavorite[]
    },
    [id, memberId, tagId],
  )

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      setLoading(true)
      const [footprints, favorites] = await Promise.all<
        TreeFotoTreeLeafFootprintData[] | null
      >([
        handleLoadFootprintData(page, limit).catch(() => null),
        handleLoadFavoriteData(page, FORKYGRAM_FAVORITES_QUERY_LIMIT).catch(
          () => null,
        ),
      ])
      setLoading(false)

      if (footprints === null || favorites === null) return null

      const favEndIndex = Math.floor(
        (footprints.length + Math.min(favorites.length, 1)) /
          FORKYGRAM_FAVORITES_QUERY_LIMIT,
      )

      return [
        ...footprints.slice(0, favEndIndex),
        ...favorites.slice(0, 1),
        ...footprints.slice(favEndIndex),
        ...favorites.slice(1),
      ]
    },
    [handleLoadFavoriteData, handleLoadFootprintData],
  )

  const handleTogglePin = useCallback(
    (shouldPin: boolean) => {
      requestData(
        shouldPin
          ? 'forkygram_post_fototree_footprint_pin'
          : 'forkygram_delete_fototree_footprint_pin',
        {
          actionType: 'execute',
          useDefaultMessage: true,
          data: {tree_id: treeId},
          onRequestSuccess: ({status}) => {
            if (status === 200) {
              // history.replaceQuery({
              //   path: 'forkygram_footprint_detail',
              //   state: {
              //     ...location.state,
              //     isPinned: shouldPin,
              //   },
              //   queryObject: {
              //     page: rawPage,
              //   },
              // })
              setPinned(shouldPin)
              showSnackbar(
                translate(
                  shouldPin
                    ? 'tree:fototreeFootprintsPinSnackbarMessage'
                    : 'tree:fototreeFootprintsUnpinSnackbarMessage',
                ),
              )
            } else if (status === 429 && shouldPin) {
              showSnackbar(
                translate('tree:fototreeFootprintsPinLimitSnackbarMessage', {
                  count: FORKYGRAM_FOOTPRINTS_MAX_ITEM,
                }),
              )
            }
          },
        },
      )
    },
    [treeId],
  )

  const handleRenderRightButton = useMemo(
    () =>
      id === memberId ? (
        <ButtonHeader
          icon={isPinned ? 'pin-2' : 'pin-2-ol'}
          label={isPinned ? 'Pin' : 'Unpin'}
          size={20}
          color="primary_4"
          disabled={loading}
          onClick={() => handleTogglePin(!isPinned)}
        />
      ) : (
        <></>
      ),
    [handleTogglePin, id, isPinned, loading, memberId],
  )

  const handleRenderContent = useMemo(
    () => (
      <ForkygramTemplateListGridContent
        innerMargin={0}
        header={false}
        backgroundColor="black"
        containerHeight="100%">
        <ForkygramFootprintPagination
          pageCount={pageCount}
          statePage={statePage}
        />
        <ListNotLazy
          renderItem={handleRenderItem}
          stateData={stateData}
          loadData={handleLoadData}
          limitPerPage={FORKYGRAM_FOOTPRINTS_QUERY_LIMIT}
          defaultPage={rawPage}
          page={rawPage}
          keyExtractor={(item) =>
            item.type === 'fotoyu' ? item.content_id : item.url
          }
          primaryExtractor={(item) => item.type === 'footprint'}
          numColumns={column}
          scrollbar={false}
        />
      </ForkygramTemplateListGridContent>
    ),
    [
      column,
      handleLoadData,
      handleRenderItem,
      pageCount,
      rawPage,
      stateData,
      statePage,
    ],
  )

  useDidMount(() => {
    if (!(memberId || tagId || treeId)) {
      history.goBack()
    }
  })

  useDidUpdate(() => {
    handleUpdatePage(rawPage)
  }, [rawPage])

  return (
    <GiftShopRootScreen
      showBackIcon
      showBadgeIcon={false}
      hideBottomNavbar={memberId !== id}
      darkBar
      darkHeader
      headerTitle={treeName}
      headerBorderColor="gray_8"
      headerSubtitle={translate('tree:fototreeFootprintsLeafCount', {count})}
      headerButtons={handleRenderRightButton}
      onClickHeader={handleClickHeader}>
      {handleRenderContent}
    </GiftShopRootScreen>
  )
}
